// Libs
import classNames from 'classnames/bind';
import { Description, Field, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
// Components, Layouts, Pages
import { BaseInputSearch, BaseLabel } from '~/components/common';
// Others
import { DEFAULT_CURRENT_PAGE, DEFAULT_DELAY_TIME, EMPTY_STRING } from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IBaseOption, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { TSelectStyleType, TSelectType } from '~/utils/type/common';
// Styles, images, icons
import { icons } from '~/assets';
import styles from './BaseSelect.module.scss';

type Props<T> = {
  height?: string | number;
  width?: string | number;
  gap?: number;
  styleType?: TSelectStyleType;
  type?: TSelectType;
  defaultSelected?: T | null;
  borderRadius?: number;
  value?: string;
  label?: string;
  iconLabel?: string;
  itemNameDefault?: string;
  labelOptions?: string;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  prefixIcon?: string;
  placeholder?: string;
  isSearch?: boolean;
  onChange?: (selectedValue: T | null) => void;
  callApiFn: (payload: IPayloadCallApiFn) => Promise<IResultCallApiFn<T>>;
  renderSelected?: (selectedValue: T) => React.ReactNode;
  renderOption?: (option: T) => React.ReactNode;
};

const cx = classNames.bind(styles);

const BaseSelect = <T extends IBaseOption>(props: Props<T>) => {
  //#region Destructuring Props
  const {
    width = '100%',
    height = 36,
    styleType = 'default',
    type = 'default',
    gap,
    defaultSelected,
    borderRadius,
    value,
    label,
    iconLabel,
    labelOptions,
    required,
    disabled,
    errorMessage,
    itemNameDefault,
    prefixIcon,
    placeholder,
    isSearch = false,
    onChange,
    callApiFn,
    renderSelected,
    renderOption,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [selectedValue, setSelectedValue] = useState<T | undefined>(undefined);
  const [listOption, setListOption] = useState<T[]>([]);
  const [searchValue, setSearchValue] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce<string>(searchValue.trim(), DEFAULT_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [hasMore, setHasMore] = useState<boolean>(true);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    handleFetchData(true);
  }, [debounceSearchKey, callApiFn]);

  useEffect(() => {
    const compareValue = value ?? defaultSelected?.value;

    if (!compareValue) {
      setSelectedValue(undefined);
      return;
    }

    const matched = listOption.find((item) => String(item.value) === String(compareValue));

    if (matched) {
      setSelectedValue(matched);
    } else if (defaultSelected) {
      setSelectedValue(defaultSelected);
    } else {
      setSelectedValue({
        label: itemNameDefault,
        value: compareValue,
      } as T);
    }
  }, [value, defaultSelected, listOption, itemNameDefault]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleFetchData = async (isFirstLoad: boolean = false) => {
    if (isLoading || (!isFirstLoad && !hasMore)) return;
    setIsLoading(true);

    try {
      const updatedPage: number = isFirstLoad ? DEFAULT_CURRENT_PAGE : page;
      const resp: IResultCallApiFn<T> = await callApiFn({ page: updatedPage, searchKey: debounceSearchKey });
      const { optionsResp, hasMoreResp = false } = resp;

      setListOption((prev) => (isFirstLoad ? optionsResp : [...prev, ...optionsResp]));
      setHasMore(hasMoreResp);
      setPage(updatedPage + 1);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptionChange = (value: T) => {
    setSelectedValue(value);
    onChange && onChange(value);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };
  //#endregion Handle Function

  return (
    <Field id='baseSelectComponent' className={cx('container')} disabled={disabled} style={{ width, gap }}>
      {label && <BaseLabel icon={iconLabel} label={label} required={required} />}

      <div className={cx('selectContainer')}>
        <Listbox value={selectedValue ?? undefined} onChange={handleOptionChange}>
          <ListboxButton className={cx('selectBtn', styleType, disabled && 'disable')} style={{ height, borderRadius }}>
            {({ open }) => (
              <>
                <div className={cx('value')}>
                  {prefixIcon && <img src={prefixIcon} alt={t('common_img_text_alt')} className={cx('iconButton')} />}
                  {!selectedValue && (
                    <>
                      {isLoading ? (
                        <div className={cx('viewLoading')}>
                          <span className={cx('loadingContent')} />
                          <span className={cx('btnText')}>{t('common_button_loading')}</span>
                        </div>
                      ) : (
                        <span className={cx('placeholder', styleType)}>
                          {placeholder || t('common_text_placeholder_select')}
                        </span>
                      )}
                    </>
                  )}
                  {selectedValue && (
                    <div className={cx('selectedValue', styleType)}>
                      {renderSelected ? renderSelected(selectedValue) : selectedValue.label}
                    </div>
                  )}
                </div>
                <img
                  src={
                    styleType === 'extra'
                      ? icons.commonIconArrowBottomWhite
                      : styleType === 'outlined'
                      ? icons.commonIconArrowBottomOutline
                      : icons.commonIconArrowBottom
                  }
                  className={cx(open ? 'iconActive' : '')}
                  alt={t('common_img_text_alt')}
                />
              </>
            )}
          </ListboxButton>

          <ListboxOptions className={cx('optionList')} transition anchor={{ to: 'bottom' }}>
            {(labelOptions || isSearch) && (
              <div className={cx('optionListHeader')}>
                {labelOptions && <h3 className={cx('optionListTitle')}>{labelOptions}</h3>}
                {isSearch && (
                  <div className={cx('optionListSearch')}>
                    <BaseInputSearch
                      placeholder={t('common_text_search', { ns: 'common/base' })}
                      width={'100%'}
                      value={searchValue || EMPTY_STRING}
                      onChange={handleSearchChange}
                    />
                  </div>
                )}
              </div>
            )}

            <div id='dynamicOptionScrollId' className={cx('scrollContainer')}>
              <InfiniteScroll
                scrollableTarget='dynamicOptionScrollId'
                dataLength={listOption.length}
                hasMore={hasMore}
                next={handleFetchData}
                height={type === 'loadMore' ? 150 : undefined}
                loader={
                  <div className={cx('viewLoading')}>
                    <span className={cx('loadingContent')} />
                    <span className={cx('btnText')}>{t('common_button_loading')}</span>
                  </div>
                }
                endMessage={
                  listOption.length <= 0 && (
                    <div className={cx('optionNoData')}>{t('common_label_no_data_available')}</div>
                  )
                }
              >
                <div className={cx('options')}>
                  {listOption.map((option) => (
                    <ListboxOption
                      key={option.value}
                      value={option}
                      className={cx('option', option.value === selectedValue?.value && 'active')}
                    >
                      <div>{renderOption ? renderOption(option) : option.label}</div>
                    </ListboxOption>
                  ))}
                </div>
              </InfiniteScroll>
            </div>
          </ListboxOptions>
        </Listbox>

        {errorMessage && <Description className={cx('errMessage')}>{errorMessage}</Description>}
      </div>
    </Field>
  );
};

export default BaseSelect;
